import { AbstractUser } from "@kvix/shared";
import { Text } from "@kvix/ui";
import { Box, Button, makeStyles } from "@material-ui/core";
import Container from "@material-ui/core/Container";
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router";
import { useLocation } from "react-use";
import SwiperCore, {
  Autoplay,
  HashNavigation,
  SwiperOptions,
} from "swiper/core";
import { Swiper, SwiperSlide } from "swiper/react";
import { CmsContext } from "../../../../cms/context";
import { HeroCarouselSlide } from "../../../../cms/models/HeroCarouselSlide";
import { useIsMobile } from "../../../../hooks/isMobile";
import { AnimatedText } from "../../../partials/AnimatedText";

SwiperCore.use([Autoplay, HashNavigation]);

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    height: "720px",

    [theme.breakpoints.down("xl")]: {
      height: "720px",
      marginBottom: theme.spacing(10),
    },
    [theme.breakpoints.down("lg")]: {
      marginBottom: theme.spacing(10),
    },
    [theme.breakpoints.down("md")]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down("sm")]: {
      height: "590px",
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(3),
    },
  },
  swiper: {
    height: "100%",

    "& .swiper-pagination": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },

    "& .swiper-pagination-bullet": {
      background: theme.palette.common.white,
      opacity: 1,
      width: 6,
      height: 6,
    },

    "& .swiper-pagination-bullet-active": {
      width: 8,
      height: 8,
    },
  },
  imageContainer: {
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-end",
    backgroundPosition: "center 30%",

    backgroundImage: (props: any) =>
      props.isVideo
        ? null
        : `linear-gradient(25deg, rgba(31,40,51,0.6) 20%, rgba(31,40,51,0.06) 55%),url(${props.url})`,
    position: "relative",

    [theme.breakpoints.down("xs")]: {
      backgroundPosition: "center center",
      alignItems: "flex-end",
    },
  },
  textContainer: {
    color: theme.palette.common.white,
    wordBreak: "break-word",
    display: "flex",
    flexDirection: "column",
    alignContent: "flex-start",
    justifyContent: "flex-start",

    [theme.breakpoints.down("xl")]: {
      paddingRight: theme.spacing(4),
      paddingLeft: theme.spacing(4),
      paddingBottom: theme.spacing(12),
    },
    [theme.breakpoints.down("lg")]: {
      paddingRight: theme.spacing(4),
      paddingLeft: theme.spacing(4),
      paddingBottom: theme.spacing(12),
    },
    [theme.breakpoints.down("md")]: {
      paddingRight: theme.spacing(4),
      paddingLeft: theme.spacing(4),
      paddingBottom: theme.spacing(8),
    },
    [theme.breakpoints.down("sm")]: {
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingBottom: theme.spacing(4),
    },
    [theme.breakpoints.down("xs")]: {
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingBottom: theme.spacing(4),
    },
  },

  video: {
    width: "100%",
    height: "100%",
    background: "#fa255f",

    //Fulhack eftersom att videos får olika bakrunder då de encoderas.
    //Möjligen lägga in ett fält i strapi med videons bakgrundsfärg?
    [theme.breakpoints.down("sm")]: {
      background: "#fb2f66",
    },
  },
  button: {
    "&.MuiButton-contained": {
      textTransform: "none",
      paddingLeft: "18px",
      paddingRight: "18px",
      marginTop: "20px",
      color: theme.palette.common.black,
      fontWeight: 700,
      zIndex: 1,

      [theme.breakpoints.down("xs")]: {
        marginTop: "12px",
      },
    },
  },
  title: {
    marginBottom: "12px",
    lineHeight: "60px",
    textShadow: "0px 0px 17px rgba(0, 0, 0, 0.2)",
    [theme.breakpoints.down("xs")]: {},
  },
  description: {
    fontWeight: 600,
    fontSize: 18,
    maxWdth: "140ch",
    textShadow: "0px 0px 17px rgba(0, 0, 0, 0.2)",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
      maxWdth: "100%",
    },
  },
  instructorTitle: {
    color: theme.palette.common.white,
    fontSize: "16px",
    lineHeight: "32px",
    fontWeight: 700,
    textShadow: "0px 0px 17px rgba(0, 0, 0, 0.2)",
    [theme.breakpoints.down("xs")]: {},
  },
}));

const options: SwiperOptions = {
  slidesPerView: 1,
  pagination: { clickable: true },
  loop: true,
  speed: 700,
  autoplay: {
    delay: 5000,
    disableOnInteraction: true,
  },
  allowTouchMove: true,
};

const getImageUrl = (slide: HeroCarouselSlide, isMobile: boolean) => {
  if (!slide.media) {
    return;
  }

  if (isMobile) {
    return (
      slide.mediaMobile?.url ||
      slide.media.formats?.large?.url ||
      slide.media.url
    );
  }

  return slide.media.url;
};

interface SlideContent {
  slide: HeroCarouselSlide;
  user: AbstractUser;
  isActive: boolean;
}
const SlideContent: React.FC<SlideContent> = (props) => {
  const { slide, user, isActive } = props;
  const isVideo =
    slide?.media.mime === "video/mp4" ||
    slide?.mediaMobile.mime === "video/mp4";
  const isMobile = useIsMobile();
  const history = useHistory();
  const videoRef = useRef<HTMLVideoElement>();
  const url = useMemo(() => {
    return getImageUrl(slide, isMobile);
  }, [slide, isMobile, getImageUrl]);

  const classes = useStyles({ isVideo, url });

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.src = url;

      if (isActive) {
        videoRef.current.play();
      }
    }
  }, [url, videoRef, isActive]);

  return (
    <div className={classes.imageContainer}>
      <Container maxWidth="xl" className={classes.textContainer}>
        {slide.instructor && (
          <AnimatedText
            scrollY={
              !user && {
                input: [50, 320],
                output: [100, 0],
              }
            }
            scrollOpacity={
              !user && {
                input: [40, 320],
                output: [0, 1],
              }
            }
          >
            <Text className={classes.instructorTitle}>{slide.instructor}</Text>
          </AnimatedText>
        )}

        {slide.title && (
          <AnimatedText
            scrollY={
              !user && {
                input: [50, 320],
                output: [140, 0],
              }
            }
            scrollOpacity={
              !user && {
                input: [40, 320],
                output: [0, 1],
              }
            }
          >
            <Text
              variant="h1"
              className={classes.title}
              color={"textSecondary"}
            >
              {slide.title}
            </Text>
          </AnimatedText>
        )}
        <AnimatedText
          scrollY={
            !user && {
              input: [50, 320],
              output: [160, 0],
            }
          }
          scrollOpacity={
            !user && {
              input: [40, 320],
              output: [0, 1],
            }
          }
        >
          <Text variant="h5" nl2br className={classes.description}>
            {slide.description}
          </Text>
        </AnimatedText>
        <AnimatedText
          scrollY={
            !user && {
              input: [50, 320],
              output: [180, 0],
            }
          }
          scrollOpacity={
            !user && {
              input: [40, 320],
              output: [0, 1],
            }
          }
        >
          {slide.buttonText && slide.buttonUrl && (
            <Button
              onClick={() => history.push(slide.buttonUrl)}
              variant="contained"
              color="primary"
              // style={{ backgroundColor: " theme.palette.common.black" }}
              // className={classes.button}
            >
              {slide.buttonText}
            </Button>
          )}
        </AnimatedText>
        {/* </div> */}
        {isVideo && <video ref={videoRef} muted className={classes.video} />}
      </Container>
    </div>
  );
};

export const HeroCarousel: React.FC<{ user: AbstractUser }> = (props) => {
  const { heroCarousel } = useContext(CmsContext);
  const classes = useStyles(props);
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState<number>(null);

  const sortedSlides = useMemo(() => {
    if (heroCarousel && heroCarousel.hero_carousel_slides) {
      return heroCarousel.hero_carousel_slides.sort((a, b) => {
        return (
          (`#${a.hash}` === location.hash ? -Infinity : a.priority) -
          (`#${b.hash}` === location.hash ? -Infinity : b.priority)
        );
      });
    }
    return null;
  }, [heroCarousel, location]);

  return (
    <Box mt={props.user ? -16 : 0}>
      <div className={classes.root}>
        {sortedSlides && (
          <Swiper
            onSlideChange={(e) => setCurrentIndex(e.activeIndex)}
            className={classes.swiper}
            {...options}
          >
            {sortedSlides.map((slide, i) => (
              <Fragment key={i}>
                {slide.media && (
                  <SwiperSlide key={i} data-hash={slide.hash}>
                    <SlideContent
                      isActive={currentIndex === i + 1}
                      slide={slide}
                      user={props.user}
                    />
                  </SwiperSlide>
                )}
              </Fragment>
            ))}
          </Swiper>
        )}
      </div>
    </Box>
  );
};
